import { cn } from '@utazzo/ui';
import { ChevronRight, CornerDownRight } from 'lucide-react';
import React, { useState } from 'react';

import {
  GroupBandRow,
  labelCell,
  MONTH_ROW,
  MONTH_TABLE,
  MonthHeaderRow,
  numberTone,
  useGroupOpen,
  valueCell,
} from './month-table';

import type {
  MarketingChannelMonth,
  MarketingChannelResponse,
  MarketingSubChannel,
} from '@utazzo/shared';

import { ReportSection } from '@/features/reports/components/ReportSection';

const inr = (amount: number): string => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return amount.toLocaleString('en-IN');
  }
};

const DASH = '-';
const count = (value: number): string => value.toLocaleString('en-IN');
const pct = (value: number | null): string => (value === null ? DASH : `${value.toFixed(2)}%`);
const money = (value: number | null): string => (value === null ? DASH : inr(value));

interface ChannelRow {
  label: string;
  indent?: boolean;
  /** A figure the eye should land on first - see `month-table.tsx`. */
  headline?: boolean;
  value: (month: MarketingChannelMonth) => string;
  /**
   * The number behind the cell, for its colour. Profit reads green, a zero
   * reads back, and a plain count takes the body colour.
   */
  signal?: (month: MarketingChannelMonth) => number | null;
  sense?: 'good';
}

/**
 * Cost rows are intentionally absent: spend is stored as one monthly total, not
 * split per channel, so a per-channel CPQ / RoAS would be invented rather than
 * measured. Everything here is derived from the leads themselves.
 */
const ROWS: ChannelRow[] = [
  {
    label: 'Profit (Sales Tracker)',
    headline: true,
    value: (m) => money(m.salesProfit),
    signal: (m) => m.salesProfit,
    sense: 'good',
  },
  { label: 'Query', headline: true, value: (m) => count(m.overall.query) },
  {
    label: 'Converted',
    value: (m) => count(m.overall.converted),
    signal: (m) => m.overall.converted,
    sense: 'good',
  },
  {
    label: '% Conversion',
    headline: true,
    value: (m) => pct(m.overall.conversionPct),
    signal: (m) => m.overall.conversionPct,
    sense: 'good',
  },
  { label: 'International Query', value: (m) => count(m.international.query) },
  {
    label: 'Converted',
    indent: true,
    value: (m) => count(m.international.converted),
    signal: (m) => m.international.converted,
    sense: 'good',
  },
  { label: '% Conversion', indent: true, value: (m) => pct(m.international.conversionPct) },
  { label: 'Domestic Query', value: (m) => count(m.domestic.query) },
  {
    label: 'Converted',
    indent: true,
    value: (m) => count(m.domestic.converted),
    signal: (m) => m.domestic.converted,
    sense: 'good',
  },
  { label: '% Conversion', indent: true, value: (m) => pct(m.domestic.conversionPct) },
  { label: 'Avg Order Value', value: (m) => money(m.overall.avgOrderValue) },
];

/**
 * The metric block rendered under a channel (or sub-channel) heading. Nesting
 * shifts every row one indent level deeper so a sub-source reads as living
 * inside its parent rather than beside it.
 */
const MetricRows = ({
  months,
  muted,
}: {
  months: MarketingChannelMonth[];
  muted?: boolean;
}): React.JSX.Element => (
  <>
    {ROWS.map((row, index) => {
      const depth = ((muted ? 1 : 0) + (row.indent ? 1 : 0)) as 0 | 1 | 2;
      return (
        <tr key={`${row.label}-${index}`} className={MONTH_ROW}>
          <th scope="row" className={labelCell({ headline: row.headline && !muted, depth, muted })}>
            {row.label}
          </th>
          {months.map((month) => (
            <td
              key={month.month}
              className={valueCell({
                headline: row.headline && !muted,
                muted,
                tone: muted || !row.signal ? undefined : numberTone(row.signal(month), row.sense),
              })}
            >
              {row.value(month)}
            </td>
          ))}
        </tr>
      );
    })}
  </>
);

/**
 * "Query Channel" - the volume / conversion / profit figures broken down by
 * lead source, with sub-sources (e.g. Instagram Form under Instagram) collapsed
 * behind an expander so the default view stays readable.
 *
 * A sub-source is a SLICE of its parent, not an addition to it, so the numbers
 * are not meant to sum down the column.
 */
export const QueryChannelTable = ({
  channels,
  loading,
  filterKeys,
}: {
  channels: MarketingChannelResponse | undefined;
  loading: boolean;
  /**
   * Restrict to the picked channels / sub-channels; empty (or omitted) shows
   * everything. Picking a sub-channel still renders its parent (expanded), so
   * the slice is always shown in the context of the source it belongs to rather
   * than floating on its own.
   */
  filterKeys?: ReadonlyArray<string>;
}): React.JSX.Element => {
  const groupOpen = useGroupOpen();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (key: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const months = channels?.months ?? [];

  // A sub-channel selection keeps its parent on screen (auto-expanded) so the
  // slice is read in context; a parent selection shows just that source.
  const picked = new Set(filterKeys ?? []);
  const visible = (channels?.channels ?? []).filter(
    (channel) =>
      picked.size === 0 ||
      picked.has(channel.key) ||
      channel.children.some((child) => picked.has(child.key)),
  );
  const forcedOpen = (channel: (typeof visible)[number]): boolean =>
    channel.children.some((child) => picked.has(child.key));

  return (
    <ReportSection title="Query Channel">
      {loading || !channels ? (
        <div className="space-y-2 py-2">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className="bg-muted h-8 animate-pulse rounded" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <p className="text-muted-foreground py-6 text-center text-sm">
          No leads in the selected range.
        </p>
      ) : (
        <>
          <table className={MONTH_TABLE}>
            <thead>
              <MonthHeaderRow label="Channel" months={months} />
            </thead>
            <tbody>
              {visible.map((channel) => {
                const open = groupOpen(expanded.has(channel.key) || forcedOpen(channel));
                return (
                  <React.Fragment key={channel.key}>
                    <GroupBandRow span={months.length} tone="entity">
                      {channel.children.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => toggle(channel.key)}
                          title={open ? 'Hide sub-sources' : 'Show sub-sources'}
                          className="flex items-center gap-1.5"
                        >
                          <ChevronRight
                            className={cn(
                              'text-muted-foreground h-3.5 w-3.5 shrink-0 transition-transform',
                              open && 'rotate-90',
                            )}
                          />
                          {channel.name}
                          <span className="bg-primary/10 text-primary rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums">
                            {channel.children.length}
                          </span>
                        </button>
                      ) : (
                        channel.name
                      )}
                    </GroupBandRow>
                    <MetricRows months={channel.months} />
                    {open &&
                      channel.children.map((child: MarketingSubChannel) => (
                        <React.Fragment key={child.key}>
                          <GroupBandRow span={months.length}>
                            <span className="flex items-center gap-1.5">
                              <CornerDownRight className="h-3.5 w-3.5 shrink-0" />
                              {child.name}
                            </span>
                          </GroupBandRow>
                          <MetricRows months={child.months} muted />
                        </React.Fragment>
                      ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          <p className="text-muted-foreground mt-3 text-xs">
            Sub-source figures are a slice of their parent source, not an addition to it, so the
            rows are not meant to sum. Spend, CPQ and RoAS are not shown here because spend is
            captured as a single monthly total rather than per channel.
          </p>
        </>
      )}
    </ReportSection>
  );
};
